import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { JiraDomainConfig } from '@/models/api-generated'

export default defineStore('jira-config-editor', () => {
	const changes = ref([] as JiraDomainConfig[])
	const changeCount = computed(() => changes.value.length)
	const isEditing = ref(true)
	const originalValues = ref(new Map<number, JiraDomainConfig>())
	const canUserEdit = ref(false)

	const addChange = (change: JiraDomainConfig) => {
		const existingIndex = changes.value.findIndex((c) => c.id === change.id)
		if (existingIndex === -1) {
			changes.value.push(change)
		} else {
			changes.value[existingIndex] = change
		}
	}

	const removeChange = (id: number) => {
		changes.value = changes.value.filter((change) => change.id !== id)
	}

	const resetChanges = () => {
		changes.value = []
	}

	const toggleEditMode = () => {
		isEditing.value = !isEditing.value
	}

	const trackOriginalValues = (domainConfig: JiraDomainConfig) => {
		if (!originalValues.value.has(domainConfig.id)) {
			originalValues.value.set(domainConfig.id, { ...domainConfig })
		}
	}

	const allFieldsMatchOriginal = (domainConfig: JiraDomainConfig): boolean => {
		const originalConfig = originalValues.value.get(domainConfig.id)
		if (!originalConfig) return false
		return Object.entries(originalConfig).every(([key, value]) => {
			return value === domainConfig[key as keyof JiraDomainConfig]
		})
	}

	const watchDomainConfig = (domainConfig: JiraDomainConfig) => {
		trackOriginalValues(domainConfig)

		const handleChange = () => {
			if (!allFieldsMatchOriginal(domainConfig)) {
				addChange(domainConfig)
			} else {
				removeChange(domainConfig.id)
			}
		}

		watch(() => domainConfig.domain, handleChange)
		watch(() => domainConfig.isActive, handleChange)
		watch(() => domainConfig.apiToken, handleChange)
		watch(() => domainConfig.email, handleChange)
		watch(() => domainConfig.automaticImport, handleChange)
		watch(() => domainConfig.admin, handleChange, { deep: true })
		watch(() => domainConfig.instance, handleChange, { deep: true })
	}

	return {
		changes,
		changeCount,
		isEditing,
		canUserEdit,
		addChange,
		removeChange,
		resetChanges,
		toggleEditMode,
		watchDomainConfig,
	}
})
